import type { FC, ReactNode } from 'react'
import type { Client } from '@/types'

import Image from 'next/image'
import { Fragment } from 'react'
import { ArrowRight, Images } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button'
import { eventTypesMarquee, heroImages } from '@/constants/content'

interface HeroProps {
    clients: Client[]
}

const MarqueeRow: FC<{ hidden?: boolean }> = ({ hidden = false }): ReactNode => {
    return (
        <ul aria-hidden={hidden ? 'true' : undefined} className='flex shrink-0 items-center'>
            {eventTypesMarquee.map((label) => (
                <li key={label} className='flex items-center'>
                    <span className='px-6 font-heading text-2xl text-white/75 italic sm:text-3xl'>
                        {label}
                    </span>
                    <span aria-hidden='true' className='size-1.5 rotate-45 bg-gold' />
                </li>
            ))}
        </ul>
    )
}

export const Hero: FC<HeroProps> = ({ clients }): ReactNode => {
    const trusted = clients.slice(0, 4)

    return (
        <section id='hero' aria-labelledby='hero-title' className='surface-night relative isolate overflow-hidden text-white'>
            <div aria-hidden='true' className='absolute inset-0 -z-10'>
                <Image
                    src={heroImages.main.src}
                    alt=''
                    fill
                    preload
                    sizes='100vw'
                    className='object-cover object-[70%_center] opacity-70'
                />
                <div className='absolute inset-0 bg-linear-to-r from-night via-night/85 to-night/25' />
                <div className='absolute inset-0 bg-linear-to-t from-night via-night/10 to-night/50' />
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute -top-48 -right-40 -z-10 size-[42rem] rounded-full bg-crimson/30 blur-[140px] motion-safe:animate-glow' />

            <div className='mx-auto grid min-h-[100svh] max-w-7xl items-end gap-12 px-6 pt-40 pb-36 lg:grid-cols-12 lg:pt-48'>
                <div className='lg:col-span-7'>
                    <p className='inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold tracking-[0.18em] text-white/80 uppercase backdrop-blur animate-in fade-in slide-in-from-bottom-2 duration-700'>
                        <span aria-hidden='true' className='size-1.5 rounded-full bg-crimson-bright' />
                        Event management in Ahmedabad, across India
                    </p>

                    <h1 id='hero-title' className='mt-7 font-heading text-6xl leading-[0.95] tracking-tight text-balance animate-in fade-in slide-in-from-bottom-4 duration-1000 sm:text-7xl lg:text-8xl'>
                        We create
                        {' '}
                        <em className='gold-gradient-text pr-2'>unforgettable</em>
                        {' '}
                        experiences
                    </h1>

                    <p className='mt-7 max-w-xl text-lg leading-relaxed text-white/75 animate-in fade-in slide-in-from-bottom-4 delay-150 duration-1000 fill-mode-both sm:text-xl'>
                        Luxury weddings, corporate launches, garba nights, concerts and stadium-scale manpower, planned and run by one dedicated team.
                    </p>

                    <div className='mt-10 flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-4 delay-300 duration-1000 fill-mode-both sm:flex-row'>
                        <ButtonLink href='/contact#inquiry' size='lg'>
                            Plan your event
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </ButtonLink>

                        <ButtonLink href='/gallery' variant='outline-light' size='lg'>
                            <Images className='size-4' aria-hidden='true' />
                            View gallery
                        </ButtonLink>
                    </div>

                    {trusted.length > 0 && (
                        <p className='mt-10 max-w-xl text-sm leading-relaxed text-white/55 animate-in fade-in delay-500 duration-1000 fill-mode-both'>
                            <span className='font-semibold text-white/80'>Trusted by </span>
                            {trusted.map((client, index) => (
                                <Fragment key={client.id}>
                                    <span data-mdlfy-cms={`clients:${client.id}:name`}>{client.name}</span>
                                    {index < trusted.length - 1 ? ', ' : ' and more'}
                                </Fragment>
                            ))}
                        </p>
                    )}
                </div>

                <div aria-hidden='true' className='relative hidden h-[30rem] lg:col-span-5 lg:block'>
                    <div className='absolute top-0 right-0 h-[24rem] w-[17rem] -rotate-3 overflow-hidden rounded-[1.75rem] border border-white/15 shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-right-8 delay-300 duration-1000 fill-mode-both'>
                        <Image src={heroImages.concert.src} alt='' fill sizes='272px' className='object-cover' />
                        <span className='absolute bottom-4 left-4 rounded-full bg-night/70 px-3 py-1.5 text-xs font-semibold backdrop-blur'>
                            Live concerts
                        </span>
                    </div>

                    <div className='absolute bottom-0 left-0 h-[13rem] w-[20rem] rotate-2 overflow-hidden rounded-[1.75rem] border border-white/15 shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-bottom-8 delay-500 duration-1000 fill-mode-both'>
                        <Image src={heroImages.stadium.src} alt='' fill sizes='320px' className='object-cover' />
                        <span className='absolute bottom-4 left-4 rounded-full bg-night/70 px-3 py-1.5 text-xs font-semibold backdrop-blur'>
                            Stadium manpower
                        </span>
                    </div>
                </div>
            </div>

            <div className='absolute inset-x-0 bottom-0 border-t border-white/10 bg-night/60 py-5 backdrop-blur'>
                <p className='sr-only'>
                    Event types we manage: {eventTypesMarquee.join(', ')}.
                </p>

                <div aria-hidden='true' className='overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]'>
                    <div className='flex w-max motion-safe:animate-marquee'>
                        <MarqueeRow />
                        <MarqueeRow hidden />
                    </div>
                </div>
            </div>
        </section>
    )
}
